/**
 * Single-slot modal control.
 *
 * One dialog at a time, addressed by id. The prototype kept a stack but never
 * pushed more than one entry, and a stack invites the nested-dialog patterns
 * that make focus management unmanageable.
 */

import { create } from 'zustand';

export type ModalId = 'account' | 'filters' | 'analysisMenu' | 'mmmImport' | 'activationInput';

interface ModalState {
  readonly open: ModalId | null;
  /** Ad-hoc payload for the open dialog, e.g. which analysis was clicked. */
  readonly payload: unknown;
  show: (id: ModalId, payload?: unknown) => void;
  close: () => void;
}

export const useModalStore = create<ModalState>()((set) => ({
  open: null,
  payload: null,
  show: (id, payload = null) => {
    set({ open: id, payload });
  },
  close: () => {
    set({ open: null, payload: null });
  },
}));

/** Typed payload reader. Returns null when the shape does not match. */
export function useModalPayload<T>(guard: (value: unknown) => value is T): T | null {
  const payload = useModalStore((s) => s.payload);
  return guard(payload) ? payload : null;
}
